import { Entry } from 'contentful';
import { NextPageWithLayout } from 'next';
import { Grid } from '../../components/Grid/Grid';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { PageNavigation } from '../../components/Navigation/PageNavigation/PageNavigation';
import { getNodesFromConnection } from '../../helpers/graphql';
import { getSpeciesUrl } from '../../helpers/urls';
import { TypeBlogPostSkeleton } from '../../interfaces/contentful';
import {
  GetBasicSpeciesInfoFromList_species_edges_node,
  GetSingleSpecies_entry,
  GetSingleSpecies_entry_catchesWithImages_edges_node,
  GetSingleSpecies_entry_speciesTips_edges_node,
  GetSpeciesTopBaits_topProductUnits_edges_node,
} from '../../interfaces/graphql';
import { GeneralInfoSection } from './components/GeneralInfoSection/GeneralInfoSection';
import { getSubNavLinks, SUB_LINK_IDS } from './helpers/speciesHelpers';
import {
  HeaderSection,
  NR_OF_RECENT_CATCHES_TO_SHOW,
} from './components/HeaderSection/HeaderSection';
import { Container } from '../../components/Container/Container';
import { TopBaitsSection } from './components/TopBaitsSection/TopBaitsSection';
import { NearbyWatersSection } from './components/NearbyWatersSection/NearbyWatersSection';
import { BiggestCatchDownloadSection } from '../../components/BiggestCatchDownloadSection/BiggestCatchDownloadSection';
import { FishingReportsGrid } from '../../components/FishingReportsGrid/FishingReportsGrid';
import {
  FISHINGREPORT_SECTION_MAX_CATCHES_MOBILE,
  FISHINGREPORT_SECTION_MAX_CATCHES_TABLET,
  FISHINGREPORT_SECTION_MAX_CATCHES_DESKTOP,
} from '../../components/FishingReportsGrid/constants/constants';
import { TipsSection } from './components/TipsSection/TipsSection';
import { BlogPostSection } from '../../components/Blog/BlogPostSection/BlogPostSection';
import { NearbyPopularSpeciesSection } from './components/NearbyPopularSpeciesSection/NearbyPopularSpeciesSection';
import { AppDownloadSection } from '../../components/AppDownloadSection/AppDownloadSection';
import { PreFooterLinksSection } from './components/PreFooterLinksSection/PreFooterLinksSection';
import { IStateInfo } from '../../components/FindUSSpecies/helpers/buildStateDataSpecies';
export interface ISpeciesProps {
  species: GetSingleSpecies_entry;
  blogPostItems: Entry<TypeBlogPostSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>[] | null;
  blogPostSlug: string | null;
  topBaits?: GetSpeciesTopBaits_topProductUnits_edges_node[];
  speciesStatesList: IStateInfo[];
  popularSpecies: GetBasicSpeciesInfoFromList_species_edges_node[];
}

export const Species: NextPageWithLayout<ISpeciesProps> = ({
  species,
  blogPostItems,
  blogPostSlug,
  topBaits,
  speciesStatesList,
  popularSpecies,
}) => {
  const {
    _id: speciesId,
    catches: { totalCount: catchCount },
    newCatchCount,
    displayName,
    exampleCatchWithImage,
    speciesTips,
    catchesWithImages,
  } = species;

  const exampleImages = getNodesFromConnection(exampleCatchWithImage?.post.images);
  const exampleImage = exampleImages.length > 0 ? exampleImages[0] : undefined;

  const recentCatches =
    getNodesFromConnection<GetSingleSpecies_entry_catchesWithImages_edges_node>(catchesWithImages);

  const hasTooFewCatches = recentCatches.length <= NR_OF_RECENT_CATCHES_TO_SHOW;

  const showHeaderSection = !hasTooFewCatches && exampleImage && exampleCatchWithImage?.post._id;

  const tipsList =
    getNodesFromConnection<GetSingleSpecies_entry_speciesTips_edges_node>(speciesTips);

  const hasTips = tipsList.length > 0;
  const hasBlogEntries = blogPostItems && blogPostItems.length > 0;
  const hasFishingReports = recentCatches.length > 0;

  return (
    <div>
      <MetaTags
        title={`Fishing for ${displayName} near you`}
        link={getSpeciesUrl(speciesId, displayName)}
        description={`Discover popular ${displayName} fishing spots near you and learn the best tactics for increasing your catches. Find the best baits for ${displayName} based on real-user catches, updated frequently.`}
        image={exampleImage?.url}
      />
      <PageNavigation
        navItems={getSubNavLinks({
          hasBiggestCatches: hasFishingReports && !hasTooFewCatches,
          hasFishingReports: hasFishingReports,
          hasProTips: hasTips,
          hasBlogEntries: hasBlogEntries ?? false,
        })}
      />
      {showHeaderSection && (
        <HeaderSection
          catchCount={catchCount}
          numberOfNewCatches={newCatchCount.totalCount}
          exampleImage={exampleImage}
          examplePostId={exampleCatchWithImage.post._id}
          recentCatches={recentCatches}
          displayName={displayName}
          speciesId={speciesId}
        />
      )}
      <Container className="mt-6 px-0 sm:mt-8 md:mt-11">
        <Grid gapType="custom" className="grid-flow-row gap-0">
          <GeneralInfoSection
            className="mb-md order-1 col-span-full md:order-1 md:col-span-6"
            species={species}
          />
          <TopBaitsSection
            className="mb-md order-2 col-span-full px-6 sm:mx-16 sm:mb-16 sm:px-8 sm:pt-6 sm:pb-2 md:order-2 md:col-span-5 md:col-start-8 md:mx-0"
            topBaits={topBaits}
            speciesName={displayName}
            speciesId={speciesId}
          />
        </Grid>
      </Container>
      <NearbyWatersSection
        className="mb-md"
        speciesId={speciesId}
        speciesName={displayName}
        title={`Top fishing spots near you to catch ${displayName}`}
      />
      {hasFishingReports && !hasTooFewCatches && (
        <BiggestCatchDownloadSection
          title={`Biggest ${displayName} catches`}
          text="Explore your local leaderboard—see the top catches in the app."
          className="mb-md scroll-mt-(--navigation-scrolltop-offset-mobile) px-6 sm:scroll-mt-(--navigation-scrolltop-offset)"
          id={SUB_LINK_IDS.BIGGEST_CATCHES}
          sourceId={speciesId}
        />
      )}
      {hasFishingReports && (
        <Container className="mb-md scroll-mt-(--navigation-scrolltop-offset-mobile) px-6 sm:scroll-mt-(--navigation-scrolltop-offset)">
          <FishingReportsGrid
            className="px-0"
            id={SUB_LINK_IDS.FISHING_REPORTS}
            fishingReports={recentCatches.map(_catch => _catch.post)}
            maxCatchesMobile={FISHINGREPORT_SECTION_MAX_CATCHES_MOBILE}
            maxCatchesTablet={FISHINGREPORT_SECTION_MAX_CATCHES_TABLET}
            maxCatchesDesktop={FISHINGREPORT_SECTION_MAX_CATCHES_DESKTOP}
            sourceId={speciesId}
            title={`Recently caught ${displayName}`}
            showFishingWaterName
          />
        </Container>
      )}
      {hasTips && (
        <TipsSection
          className="mb-md scroll-mt-(--navigation-scrolltop-offset-mobile) px-6 sm:scroll-mt-(--navigation-scrolltop-offset)"
          tipsList={tipsList}
        />
      )}
      {blogPostItems && blogPostItems.length > 0 && (
        <BlogPostSection
          className="mb-md scroll-mt-(--navigation-scrolltop-offset-mobile) px-6 sm:scroll-mt-(--navigation-scrolltop-offset)"
          blogPostItems={blogPostItems}
          id={SUB_LINK_IDS.BLOG_ENTRIES}
          linkTo={blogPostSlug ? `/blog/species/${blogPostSlug}` : '/blog/species'}
          linkToText="See related posts"
          speciesId={speciesId}
        />
      )}
      <AppDownloadSection className="mb-md !py-0" hasAppRating />
      <NearbyPopularSpeciesSection
        className="scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset)"
        id={SUB_LINK_IDS.NEARBY_SPECIES}
        clickEventSourceId={speciesId}
      />
      <PreFooterLinksSection
        speciesName={displayName}
        popularSpecies={popularSpecies}
        speciesStatesList={speciesStatesList}
      />
    </div>
  );
};
